import {
  ActualizacionFoto,
  EstadoActualizacionCc,
  FotoProyecto,
  FotoQa,
  ProyectoResumen,
  ReporteEmitido,
} from "src/app/shared/model/calidad-codigo/calidad-codigo.model";
import { credencialRechazada } from "../cc-estados";
import { fechaHora } from "../cc-formato";

/**
 * El reporte al día (12/10/2026, pedido de Norman: «que si hay nueva actualización también la contenga el PDF»):
 * reporteEmitir.do corre una pasada del sincronizador (origen REPORTE) antes de congelar la foto y la foto dice cómo
 * le fue en `actualizacion` (docs/CALIDAD_CODIGO.md §G6). Aquí se lee esa llave para los globos de los botones
 * mientras se emite, el flotante al terminar (y si la pantalla se recarga) y la línea de fuente del PDF.
 */

/** El globo de los botones mientras se emite: primero se consulta SonarQube y luego se congela la foto. */
export const EMITIENDO_PDF = "Consultando SonarQube y emitiendo el reporte PDF…";
export const EMITIENDO_EXCEL = "Consultando SonarQube y emitiendo el reporte en Excel…";

/** Por qué el reporte salió con la última foto guardada, en palabras de cualquiera; los demás estados sí consultaron. */
const SIN_CONSULTA: Partial<Record<EstadoActualizacionCc, string>> = {
  SIN_CREDENCIAL: "falta la credencial de SonarQube",
  OCUPADO: "otra actualización seguía en curso",
};

/**
 * ERROR no siempre es que SonarQube no respondiera: también lo dan un 401, un 403, un proyecto que ya no está en
 * SonarQube o una falla del propio COS. Se dice lo que siempre es cierto y, si el error de la pasada lo dice, que
 * rechazó la credencial (la misma regla que la franja del tablero). El aviso y el pie del PDF, congelado con su
 * folio, nunca afirman una causa que no fue.
 */
const ERROR_CREDENCIAL = "SonarQube rechazó la credencial del COS";
const ERROR_CONSULTA = "la consulta a SonarQube falló";

/**
 * Lo que la pantalla enseña de un proyecto y que la foto de su reporte también congela: el renglón del tablero
 * (ProyectoResumen) o el detalle (ProyectoDetalle, que además trae la clave del análisis).
 */
export type EnPantallaCc = Pick<ProyectoResumen, "id" | "estado" | "fechaAnalisis" | "medidas"> & { analisisKey?: string | null };

/** El formato que se emitió: cambia la cola del mensaje («emitido y descargado» o «el Excel se descargó»). */
export type FormatoEmisionCc = "PDF" | "EXCEL";

/** El tipo de flotante (uno de los métodos de FlotantesCcService). */
export type TipoAvisoEmisionCc = "exito" | "aviso" | "error";

/** El flotante con que termina una emisión y si la pantalla se recarga para decir lo mismo que el reporte. */
export interface AvisoEmisionCc {
  tipo: TipoAvisoEmisionCc;
  texto: string;
  recargar: boolean;
}

/** Cómo termina cada mensaje, por formato: de siempre, con lo último de SonarQube o, tras el porqué, sin consultar. */
const COLA: Record<FormatoEmisionCc, { deSiempre: string; conNovedades: string; sinConsulta: string }> = {
  PDF: {
    deSiempre: " emitido y descargado.",
    conNovedades: " emitido y descargado con lo último de SonarQube.",
    sinConsulta: ".",
  },
  EXCEL: {
    deSiempre: " emitido; el Excel se descargó.",
    conNovedades: " emitido con lo último de SonarQube; el Excel se descargó.",
    sinConsulta: ". El Excel se descargó.",
  },
};

/** La consulta a SonarQube que congeló el folio; null sin foto o en un folio anterior al 12/10/2026 (sin la llave). */
export function actualizacionDe(reporte: ReporteEmitido | null | undefined): ActualizacionFoto | null {
  const datos = reporte?.datos as Partial<FotoProyecto & FotoQa> | null | undefined;
  return datos?.actualizacion ?? null;
}

/** La consulta sí corrió (OK o PARCIAL) y trajo análisis nuevos o cambios: lo que se ve en pantalla ya no es lo último. */
export function trajoNovedades(a: ActualizacionFoto | null | undefined): boolean {
  if (a?.estado !== "OK" && a?.estado !== "PARCIAL") {
    return false;
  }
  return (a.analisisNuevos ?? 0) > 0 || (a.cambios ?? 0) > 0;
}

/** Por qué salió con la última foto guardada (ERROR, SIN_CREDENCIAL u OCUPADO); vacío si sí consultó o no se sabe. */
export function porQueSinConsulta(a: ActualizacionFoto | null | undefined): string {
  if (a?.estado === "ERROR") {
    return credencialRechazada(a.error) ? ERROR_CREDENCIAL : ERROR_CONSULTA;
  }
  return a ? (SIN_CONSULTA[a.estado] ?? "") : "";
}

/**
 * La foto del reporte de un proyecto ya no dice lo mismo que la pantalla: otro veredicto, otro análisis o alguna cifra
 * distinta. Pasa aunque esta emisión no trajera novedades: la pasada programada pudo guardar un análisis o un cambio
 * después de que se cargó la pantalla (ni el tablero ni el detalle se refrescan solos). Sin foto de proyecto (un folio
 * de QA o sin datos), sin pantalla o con la de otro proyecto, no hay qué comparar.
 */
export function fotoDifiere(reporte: ReporteEmitido | null | undefined, enPantalla: EnPantallaCc | null | undefined): boolean {
  const foto = reporte?.datos as Partial<FotoProyecto> | null | undefined;
  if (!foto?.veredicto || !enPantalla) {
    return false;
  }
  if (foto.proyecto?.id !== enPantalla.id) {
    return false;
  }
  return (
    foto.veredicto.estado !== enPantalla.estado ||
    !mismaFecha(foto.analisis?.fecha, enPantalla.fechaAnalisis) ||
    otroAnalisis(foto.analisis?.analisisKey, enPantalla.analisisKey) ||
    !mismasMedidas(foto.medidas, enPantalla.medidas)
  );
}

/** Dos fechas ISO del mismo instante (el servidor las escribe igual; si no, se comparan como instantes); sin ninguna, iguales. */
function mismaFecha(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) {
    return !a && !b;
  }
  return a === b || Date.parse(a) === Date.parse(b);
}

/** La clave del análisis solo cuenta si la pantalla la tiene (el detalle; el renglón del tablero no la trae). */
function otroAnalisis(enFoto: string | null | undefined, enPantalla: string | null | undefined): boolean {
  return enPantalla !== undefined && (enFoto ?? null) !== (enPantalla ?? null);
}

/** Las mismas cifras crudas, clave por clave: la foto y la pantalla salen del mismo armado del servidor. */
function mismasMedidas(a: Record<string, string> | null | undefined, b: Record<string, string> | null | undefined): boolean {
  const x = a ?? {};
  const y = b ?? {};
  const claves = Object.keys(x);
  return claves.length === Object.keys(y).length && claves.every((k) => x[k] === y[k]);
}

/**
 * El flotante al terminar de emitir, según la consulta a SonarQube de esa emisión. `cabeza` dice qué se emitió
 * («Reporte CC-20261012-0007 de POS SaaS»):
 *   con novedades (OK o PARCIAL con análisis nuevos o cambios): éxito «… con lo último de SonarQube»;
 *   sin novedades, PARCIAL sin novedades o un folio sin la llave: el éxito de siempre;
 *   ERROR, SIN_CREDENCIAL u OCUPADO: aviso «… emitido con la última foto guardada: <por qué>».
 * La pantalla se recarga si hubo novedades o si la foto ya no dice lo mismo que `enPantalla` (fotoDifiere), con
 * cualquier estado de la consulta.
 */
export function avisoDeEmision(
  cabeza: string,
  reporte: ReporteEmitido,
  formato: FormatoEmisionCc = "PDF",
  enPantalla?: EnPantallaCc | null,
): AvisoEmisionCc {
  const a = actualizacionDe(reporte);
  const cola = COLA[formato];
  const porQue = porQueSinConsulta(a);
  const novedades = trajoNovedades(a);
  const recargar = novedades || fotoDifiere(reporte, enPantalla);
  if (porQue) {
    return { tipo: "aviso", texto: `${cabeza} emitido con la última foto guardada: ${porQue}${cola.sinConsulta}`, recargar };
  }
  return { tipo: "exito", texto: cabeza + (novedades ? cola.conNovedades : cola.deSiempre), recargar };
}

/** Una emisión que no salió (el servidor contestó un error o no hubo conexión): un flotante de error, sin recargar. */
export function avisoDeError(texto: string): AvisoEmisionCc {
  return { tipo: "error", texto, recargar: false };
}

/**
 * Lo que dice la línea de fuente del PDF sobre la consulta al emitir: «Consultado en SonarQube al emitir, 12/10/2026
 * 10:45.» (con «; parte de la consulta falló» si fue PARCIAL), «Emitido con la última foto guardada en el COS: la
 * consulta a SonarQube falló.» (o el otro porqué) o nada en un folio anterior (sin la llave) o con un estado que no se
 * conoce.
 */
export function textoConsultaPdf(a: ActualizacionFoto | null | undefined): string {
  const porQue = porQueSinConsulta(a);
  if (porQue) {
    return `Emitido con la última foto guardada en el COS: ${porQue}.`;
  }
  if (a?.estado !== "OK" && a?.estado !== "PARCIAL") {
    return "";
  }
  const cuando = a.fecha ? `, ${fechaHora(a.fecha)}` : "";
  const parcial = a.estado === "PARCIAL" ? "; parte de la consulta falló" : "";
  return `Consultado en SonarQube al emitir${cuando}${parcial}.`;
}
